import { BadgeLeadingIconSlot, BadgeTrailingIconSlot } from '@magicschool/spellbook/components/Badge/slots';
import { Text } from '@magicschool/spellbook/components/Text';
import { useSlots } from '@magicschool/spellbook/hooks/useSlots';
import { cn } from '@magicschool/spellbook/lib/utils';
import type { VariantProps } from 'class-variance-authority';
import { cva } from 'class-variance-authority';

export type BadgeProps = React.ComponentProps<'div'> & VariantProps<typeof badgeVariants>;

export const badgeVariants = cva(
  [
    'inline-flex',
    'items-center',
    'w-fit',
    'whitespace-nowrap',
    'shrink-0',
    'text-ellipsis',
    'transition-background',
    'transition-colors',
    'outline-none',
    'rounded-xs',
    'text-default-text',
  ],
  {
    variants: {
      /** The visual appearance of the badge. */
      variant: {
        default: 'bg-default-muted-background text-default-text-strong',
        primary: 'bg-primary-muted-background text-primary-text-strong',
        success: 'bg-success-muted-background text-success-text-strong',
        warning: 'bg-warning-muted-background text-warning-text-strong',
        error: 'bg-error-muted-background text-error-text-strong',
        info: 'bg-info-muted-background text-info-text-strong',
        accent1: 'bg-accent-1-muted-background text-accent-1-text-strong',
        accent2: 'bg-accent-2-muted-background text-accent-2-text-strong',
        defaultTransparent: 'bg-transparent text-default-text-strong',
        primaryTransparent: 'bg-transparent text-primary-text-strong',
        successTransparent: 'bg-transparent text-success-text-strong',
        warningTransparent: 'bg-transparent text-warning-text-strong',
        errorTransparent: 'bg-transparent text-error-text-strong',
        infoTransparent: 'bg-transparent text-info-text-strong',
        accent1Transparent: 'bg-transparent text-accent-1-text-strong',
        accent2Transparent: 'bg-transparent text-accent-2-text-strong',
      },
      /** The size of the badge. */
      size: {
        xs: 'h-5 max-h-5 p-1 gap-0',
        sm: 'h-6 max-h-6 p-1 gap-0.5',
        md: 'h-7 max-h-7 p-1 gap-0.5',
      },
    },
  },
);

const BadgeBase: React.FC<BadgeProps> = ({ children, className, variant = 'default', size = 'md', ...props }) => {
  const iconColor = variant?.split('Transparent')[0] || variant;
  const { slots, rest } = useSlots(children, {
    leadingIcon: { Component: BadgeLeadingIconSlot, props: { color: iconColor, size } },
    trailingIcon: { Component: BadgeTrailingIconSlot, props: { color: iconColor, size } },
  });
  return (
    <div className={cn(badgeVariants({ variant, size }), className)} data-spellbook-name="badge" {...props}>
      {slots.leadingIcon}
      <Text variant={size === 'xs' ? 'badge-1' : 'label-1'} element="span" color="inherit" className={size === 'xs' ? 'px-0.5' : 'px-1'}>
        {rest}
      </Text>
      {slots.trailingIcon}
    </div>
  );
};

/**
 * Static label for displaying status, categories, tags, or feature indicators. Not interactive.
 *
 * ### When to use
 * - For status indicators (e.g., "Active", "Paused", "Error")
 * - For category labels or tags (e.g., "New", "Beta", "Pro Feature")
 * - For metadata display (e.g., role labels, plan tiers)
 * - For feature flags or indicators that need semantic color coding
 *
 * ### When NOT to use
 * - For notification counts or unread indicators: use `NotificationBadge` instead — it has `role="status"` and `aria-live` for screen reader announcements
 * - For interactive elements: use `Button` instead
 * - For removable tags: use `InputChip` instead
 *
 * ### API
 * - `variant`: Semantic color with muted background: `default`, `primary`, `success`, `warning`, `error`, `info`, `accent1`, `accent2`. Add `Transparent` suffix (e.g., `primaryTransparent`) for no background.
 * - `size`: Badge dimensions: `xs`, `sm`, `md` (default)
 *
 * ### Composition (slots)
 * - `<Badge.LeadingIcon>`: Icon before text. Pass `name` prop with a Lucide icon name. Only include when adding an icon — not rendered by default.
 * - `<Badge.TrailingIcon>`: Icon after text. Pass `name` prop with a Lucide icon name. Only include when adding an icon — not rendered by default.
 *
 * ### Accessibility
 * - Badge is presentational — it does not have an interactive role
 * - Color alone should not convey meaning — always include text that describes the status
 * - For dynamic status that should announce to screen readers, use another component with `role="status"` and `aria-live` (e.g., `NotificationBadge`)
 *
 * ### Additional notes
 * - For Spellbook contributors: `badgeVariants` can be used internally to apply badge styling to custom components.
 * - Icon color automatically matches the badge variant
 */
export const Badge = Object.assign(BadgeBase, {
  LeadingIcon: BadgeLeadingIconSlot,
  TrailingIcon: BadgeTrailingIconSlot,
});
